export const BAZAAR_CURRENCY_DEFAULTS_CONFIG = "bazaar_currency_defaults";
export const ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY = "zero_decimal_currency_codes";
export const MICROS_PER_CURRENCY_UNIT = 1_000_000;

export const FALLBACK_ZERO_DECIMAL_CURRENCY_CODES = ["CLP", "JPY", "KRW", "VND"] as const;

export type ZeroDecimalCurrencyCodes = readonly string[];

export function normalizeCurrencyCode(currency: string | null | undefined): string | null {
  const normalizedCurrency = currency?.trim().toUpperCase();
  return normalizedCurrency || null;
}

export function getZeroDecimalCurrencyCodes(value: unknown): ZeroDecimalCurrencyCodes {
  if (!Array.isArray(value)) {
    return FALLBACK_ZERO_DECIMAL_CURRENCY_CODES;
  }

  const currencyCodes: string[] = [];
  for (const rawCurrencyCode of value) {
    if (typeof rawCurrencyCode !== "string") {
      continue;
    }
    const normalizedCurrencyCode = normalizeCurrencyCode(rawCurrencyCode);
    if (normalizedCurrencyCode != null) {
      currencyCodes.push(normalizedCurrencyCode);
    }
  }

  return currencyCodes.length > 0 ? currencyCodes : FALLBACK_ZERO_DECIMAL_CURRENCY_CODES;
}

export function isZeroDecimalCurrency(
  currency: string | null | undefined,
  zeroDecimalCurrencyCodes: ZeroDecimalCurrencyCodes = FALLBACK_ZERO_DECIMAL_CURRENCY_CODES,
): boolean {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (normalizedCurrency == null) {
    return false;
  }
  return zeroDecimalCurrencyCodes.includes(normalizedCurrency);
}

export function getCurrencyFractionDigits(
  currency: string | null | undefined,
  zeroDecimalCurrencyCodes: ZeroDecimalCurrencyCodes = FALLBACK_ZERO_DECIMAL_CURRENCY_CODES,
): 0 | 2 {
  return isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) ? 0 : 2;
}

export function formatCurrencyAmountInput({
  amount,
  currency,
  zeroDecimalCurrencyCodes,
}: {
  amount: number;
  currency: string | null | undefined;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): string {
  return amount.toFixed(getCurrencyFractionDigits(currency, zeroDecimalCurrencyCodes));
}

export function parseConfiguredCurrencyAmount({
  value,
  currency,
  zeroDecimalCurrencyCodes,
}: {
  value: unknown;
  currency: string | null | undefined;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): string | undefined {
  const amount =
    typeof value === "number" ? value
    : typeof value === "string" ? Number(value.trim())
    : NaN;
  if (!Number.isFinite(amount) || amount <= 0) {
    return undefined;
  }
  if (isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) && !Number.isInteger(amount)) {
    return undefined;
  }
  return formatCurrencyAmountInput({ amount, currency, zeroDecimalCurrencyCodes });
}

export function currencyAmountToMicros({
  amount,
  currency,
  zeroDecimalCurrencyCodes,
}: {
  amount: number;
  currency: string | null | undefined;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): number | null {
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  if (isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) && !Number.isInteger(amount)) {
    return null;
  }
  return Math.round(amount * MICROS_PER_CURRENCY_UNIT);
}
